const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api.php';

function parseImages(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch {}
    return [trimmed];
  }
  return [];
}

function normalizeMachine(row) {
  if (!row || typeof row !== 'object') return row;
  const images = parseImages(row.images ?? row.image_urls ?? []);
  const featured = typeof row.featured === 'boolean'
    ? row.featured
    : ['1', 'true', 'yes', 'on'].includes(String(row.featured ?? '').toLowerCase());

  return {
    ...row,
    images,
    featured,
    createdAt: row.createdAt || row.created_at || null,
    updatedAt: row.updatedAt || row.updated_at || null,
    status: row.status || row.state || 'Available',
    keyFeatures: row.keyFeatures || row.specs || row.features || '',
  };
}

function normalizeCategory(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.id || row.slug || row.label || '',
    label: row.label || row.name || row.id || '',
    icon: row.icon || '📦',
  };
}

export function buildApiUrl(resource, params = {}) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(API_BASE, origin);
  url.searchParams.set('resource', resource);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}

export function buildApiScriptUrl(scriptName) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(API_BASE, origin);
  const directory = url.pathname.slice(0, url.pathname.lastIndexOf('/') + 1);
  url.pathname = `${directory}${scriptName}`;
  url.search = '';
  url.hash = '';
  return url.toString();
}

export async function uploadMachineImage(file) {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(buildApiScriptUrl('upload-image.php'), {
    method: 'POST',
    body: formData,
  });
  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.url) {
    throw new Error(payload?.error || 'Image upload failed. Please try again.');
  }

  return payload.url;
}

async function request(resource, options = {}) {
  const response = await fetch(buildApiUrl(resource, options.params), {
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const rawText = await response.text();
  let payload = null;

  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = rawText;
    }
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && payload.error
      ? payload.error
      : 'Request failed';
    throw new Error(message);
  }

  return payload;
}

export async function loadMachines() {
  const data = await request('machines');
  return Array.isArray(data) ? data.map(normalizeMachine) : [];
}

export async function createMachine(payload) {
  return request('machines', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateMachine(id, payload) {
  return request('machines', {
    method: 'PUT',
    params: { id },
    body: JSON.stringify(payload),
  });
}

export async function deleteMachine(id) {
  return request('machines', { method: 'DELETE', params: { id } });
}

export async function loadCategories() {
  const data = await request('categories');
  return Array.isArray(data) ? data.map(normalizeCategory) : [];
}

export async function createCategory(payload) {
  return request('categories', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCategory(id, payload) {
  return request('categories', {
    method: 'PUT',
    params: { id },
    body: JSON.stringify(payload),
  });
}

export async function deleteCategory(id) {
  return request('categories', { method: 'DELETE', params: { id } });
}

export async function loadAdminPassword() {
  const data = await request('settings', { params: { key: 'admin_password' } });
  return typeof data === 'string' ? data : '';
}

export async function saveAdminPassword(value) {
  return request('settings', {
    method: 'POST',
    body: JSON.stringify({ key: 'admin_password', value }),
  });
}
